import dynamic from 'next/dynamic'
import router from 'next/router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons'
import classNames from 'classnames'
import styles from './_video.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import { SongPageContext } from '..'
import Dropdown, {
  DropdownOption,
  DropdownProps
} from '../../../../components/Dropdown'
import { t } from '../../../../utils'
import { useAuth } from '../../../../utils/auth'
import SongHeader from './SongHeader'
import { ModalsContext } from '../../../../utils/modals'
import useDropdownDirection from '../../../../utils/hooks/useDropdownDirection'
import { Song } from '../../../../__generated__/graphql'
import { isIPad } from '../../../../utils/deviceDetection'

const Mixer = dynamic(() => import('./Mixer'), {
  ssr: false
})

export let AllInstruments: DropdownOption[] = []

const defineInstruments = (instruments: DropdownOption[]): DropdownOption[] => {
  AllInstruments = AllInstruments.concat(instruments)
  return instruments
}

export const ElectricGuitarOptions = defineInstruments([
  { value: 'el_both', label: t('Both Electrics') },
  { value: 'el_one', label: t('Electric 1') },
  { value: 'el_two', label: t('Electric 2') },
  { value: 'el_lesson_one', label: t('Electric 1 Lesson') },
  { value: 'el_lesson_two', label: t('Electric 2 Lesson') }
])

export const AccousticGuitarOptions = defineInstruments([
  { value: 'ac_both', label: t('Both Acoustics') },
  { value: 'ac_one', label: t('Acoustic 1') },
  { value: 'ac_two', label: t('Acoustic 2') },
  { value: 'ac_lesson_one', label: t('Acoustic 1 Lesson') },
  { value: 'ac_lesson_two', label: t('Acoustic 2 Lesson') }
])

export const BassOptions = defineInstruments([
  { value: 'bass', label: t('Bass') },
  { value: 'bass_lesson', label: t('Bass Lesson') }
])

export const KeyOptions = defineInstruments([
  { value: 'keys_both', label: t('Both Keys') },
  { value: 'keys_main', label: t('Main Keys') },
  { value: 'keys_aux', label: t('Aux Keys') },
  { value: 'keys_main_lesson', label: t('Main Keys Lesson') },
  { value: 'keys_aux_lesson', label: t('Aux Keys Lesson') }
])

export const DrumOptions = defineInstruments([
  { value: 'drums', label: 'Drums' },
  { value: 'drums_lesson', label: 'Drum Lesson' }
])

export const VocalOptions = defineInstruments([
  { value: 'vocal_all', label: t('All Vocals') },
  { value: 'vocal_tenor_lead', label: t('Lead/Tenor') },
  { value: 'vocal_alto_lead', label: t('Lead/Alto') },
  { value: 'vocal_soprano_lead', label: t('Lead/Soprano') },
  { value: 'vocal_tenor', label: t('Tenor') },
  { value: 'vocal_tenor_two', label: t('Tenor 2') },
  { value: 'vocal_alto', label: t('Alto') },
  { value: 'vocal_alto_two', label: t('Alto 2') },
  { value: 'vocal_soprano', label: t('Soprano') },
  { value: 'vocal_soprano_two', label: t('Soprano 2') }
])

const InstrumentsLine = ({ songWithVideo }) => {
  const { isAuthenticated } = useAuth()
  const video = songWithVideo?.video || {}
  const presentOptions = (instrument: DropdownOption) =>
    (!isAuthenticated && !songWithVideo?.video) || video[instrument.value]
  const electricGuitarOptions = ElectricGuitarOptions.filter(presentOptions)
  const bassOptions = BassOptions.filter(presentOptions)
  const accousticGuitarOptions = AccousticGuitarOptions.filter(presentOptions)
  const keyOptions = KeyOptions.filter(presentOptions)
  const drumOptions = DrumOptions.filter(presentOptions)
  const vocalOptions = VocalOptions.filter(presentOptions)

  const { setPlayingVideoInstrument } = useContext(SongPageContext)
  const { openModal, closeModal } = useContext(ModalsContext)

  const openSignInModal = () => {
    openModal({
      name: 'SignIn',
      props: {
        onClose: closeModal
      },
      modalTitle: t('Sign In')
    })
  }

  //this function get option name and all options and return the changeOptionName function witch can change the option name
  const changeOptionValueContainer =
    (placeholder, options) =>
    (name: string, parameters: string[], newData: [string, string][]) => {
      if (placeholder === name) {
        const haveParameterKey = options.find((elem) =>
          parameters.find((param) => param === elem.value)
        )
        if (!haveParameterKey) {
          newData.forEach((elem) => {
            const index = options.findIndex(
              (option) => option.value === elem[0]
            )

            options[index].label = elem[1]
          })
        }
      }
    }

  const [currentSelected, setCurrentSelected] = useState('')

  const dropdownOptions = (
    name: string,
    options: DropdownOption[]
  ): DropdownProps => {
    const changeOptionName = changeOptionValueContainer(name, options)

    changeOptionName(
      'Keys',
      ['keys_both'],
      [
        ['keys_main', 'Keys'],
        ['keys_main_lesson', 'Keys Lesson']
      ]
    )

    changeOptionName(
      'Electric',
      ['el_both'],
      [
        ['el_one', 'Electric'],
        ['el_lesson_one', 'Electric Lesson']
      ]
    )

    changeOptionName(
      'Acoustic',
      ['ac_both', 'ac_two'],
      [['ac_one', 'Acoustic']]
    )

    return {
      placeholder: name,
      onChange: (option: DropdownOption) => {
        if (isAuthenticated || songWithVideo?.video) {
          setPlayingVideoInstrument(option.value)
          setCurrentSelected(name)
        } else {
          openSignInModal()
        }
      },
      options: options,
      currentSelected,
      className: styles.instrumentDropdown
    }
  }

  const { containerRef, dropdownStyle } = useDropdownDirection()

  return (
    <div ref={containerRef} className={`columns ${dropdownStyle}`}>
      {electricGuitarOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown
            {...dropdownOptions(t('Electric'), electricGuitarOptions)}
          />
        </div>
      )}
      {accousticGuitarOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown
            {...dropdownOptions(t('Acoustic'), accousticGuitarOptions)}
          />
        </div>
      )}
      {bassOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown {...dropdownOptions(t('Bass'), bassOptions)} />
        </div>
      )}
      {keyOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown {...dropdownOptions(t('Keys'), keyOptions)} />
        </div>
      )}
      {drumOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown {...dropdownOptions(t('Drums'), drumOptions)} />
        </div>
      )}
      {vocalOptions.length > 0 && (
        <div className={styles.dropdownItems}>
          <Dropdown {...dropdownOptions(t('Vocals'), vocalOptions)} />
        </div>
      )}
    </div>
  )
}

const SongInstruments: React.FC<{
  song: Song
}> = ({ song }) => {
  const [isRouteChanged, setIsRouteChanged] = useState(false)

  useEffect(() => {
    const onRouterChange = () => setIsRouteChanged(true)
    router.events.on('routeChangeStart', onRouterChange)

    return () => {
      router.events.off('routeChangeStart', onRouterChange)
    }
  }, [])

  const [selectedMixerKey, setSelectedMixerKey] = useState(song.key)

  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [mixerIsPlaying, setMixerIsPlaying] = useState(false)
  const [isMixerShown, setisMixerShown] = useState(false)

  const defaultMixerValueRef = useRef<boolean>(false)

  const { songWithVideo } = useContext(SongPageContext)

  return (
    <>
      <div className="section is-hidden-print">
        <div
          className={classNames(
            'container',
            styles.instrumentDropdownContainer
          )}
        >
          <SongHeader
            song={song}
            setIsPlaying={setIsPlaying}
            isPlaying={isPlaying}
            mixerIsPlaying={mixerIsPlaying}
          />
          <InstrumentsLine songWithVideo={songWithVideo} />
        </div>
      </div>

      {song.audioMixerTracks?.length > 0 && !isIPad() && (
        <div className="is-relative is-hidden-mobile">
          <button
            className={styles.mixerToggler}
            onClick={() => {
              defaultMixerValueRef.current = true
              setisMixerShown(!isMixerShown)
            }}
          >
            {isMixerShown ? (
              <span>
                {t('Close')} <FontAwesomeIcon icon={faCaretUp} />
              </span>
            ) : (
              <span>
                {t('Mixer')} <FontAwesomeIcon icon={faCaretDown} />
              </span>
            )}
          </button>

          {defaultMixerValueRef.current && (
            <Mixer
              song={song}
              selectedMixerKey={selectedMixerKey}
              setSelectedMixerKey={setSelectedMixerKey}
              isPlaying={isPlaying}
              setMixerIsPlaying={setMixerIsPlaying}
              isRouteChanged={isRouteChanged}
              isMixerShown={isMixerShown}
              setisMixerShown={setisMixerShown}
            />
          )}
        </div>
      )}
    </>
  )
}
export default SongInstruments
